import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

// The case runs in four stages. Every page says which stage it belongs to, and hands the reader to the next one.
export const STAGES = [
  { n: 1, key: "history", label: "Kenya's Football History", to: "/hall-of-fame" },
  { n: 2, key: "build", label: "Building The Game", to: "/plans" },
  { n: 3, key: "invest", label: "Paying For It", to: "/value-case" },
  { n: 4, key: "national", label: "The National Game", to: "/national-game" },
];

const HANDOFF = {
  history: {
    heading: "So what follows",
    body:
      "Every professional and every first on this site was produced with no county academy, no graded pathway and no camp base within reach of home. That is the argument, not the achievement. If Kenya exports this much talent by accident, the case for building it on purpose writes itself.",
    to: "/plans",
    cta: "Stage two: building the game",
  },
  build: {
    heading: "So what follows",
    body:
      "Courts, leagues, coaches, academies and licensed clubs are the machine. On their own they are a shopping list. What turns them into a case is what they cost, what they return and who pays for them.",
    to: "/value-case",
    cta: "Stage three: paying for it",
  },
  invest: {
    heading: "So what follows",
    body:
      "Investment is only worth arguing for if it ends somewhere. It ends with Harambee Stars picked from eighteen county catchments instead of one city — players who keep their club contracts and their weekend teams, and train for national duty within reach of home.",
    to: "/national-game",
    cta: "Stage four: the national game",
  },
  national: {
    heading: "Back where it started",
    body:
      "A national team picked from eighteen catchments is how the talent already in the Hall of Fame stops being an accident. Then the archive becomes a pipeline, and the next generation is produced on purpose.",
    to: "/apply",
    cta: "Send us your research",
  },
};

/** The four-stage progress bar, shown at the top of every page in the case. */
export const StageBar = ({ stage }) => {
  const current = STAGES.find((s) => s.key === stage) || STAGES[0];
  return (
    <div className="border-b border-zinc-900 bg-[#070707]" data-testid="stage-bar">
      <div className="mx-auto flex max-w-[1500px] flex-wrap items-center gap-x-1 gap-y-2 px-5 py-3 md:px-10">
        {STAGES.map((s, i) => (
          <span key={s.key} className="flex items-center gap-1">
            <Link
              to={s.to}
              data-testid={`stage-link-${s.n}`}
              className={`flex items-center gap-2 px-2 py-1 text-[0.55rem] font-bold uppercase tracking-[0.16em] transition-colors ${
                s.key === current.key ? "bg-maasai text-white" : "text-zinc-500 hover:text-white"
              }`}
            >
              <span className="mono">{s.n}</span>
              {s.label}
            </Link>
            {i < STAGES.length - 1 ? <span className="text-zinc-700">/</span> : null}
          </span>
        ))}
      </div>
    </div>
  );
};

/** The closing panel that hands the reader from one stage to the next. */
export const StageClose = ({ stage }) => {
  const h = HANDOFF[stage] || HANDOFF.history;
  const current = STAGES.find((s) => s.key === stage) || STAGES[0];
  return (
    <section className="border-t border-zinc-900 bg-[#0b0b0b] py-16 md:py-20" data-testid="stage-close">
      <div className="mx-auto flex max-w-[1500px] flex-col justify-between gap-8 px-5 md:flex-row md:items-end md:px-10">
        <div className="max-w-2xl">
          <p className="eyebrow">
            Stage {current.n} of 4 — {current.label}
          </p>
          <h2 className="display-2 mt-4">{h.heading}</h2>
          <p className="body-copy mt-5">{h.body}</p>
        </div>
        <Link to={h.to} className="btn-primary shrink-0" data-testid="stage-close-cta">
          {h.cta} <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
};
